import dayjs from 'dayjs';

import {
  APPOINTMENTS_DIAGNOSIS,
  APPOINTMENTS_SHOW_VISIT_TIME,
  APPOINTMENTS_YEAR,
} from '@constants/extension-codes';
import { APPT_SERVICE_TYPE_MAP } from '@constants/option-types/appointment';

import {
  formatFhirDate,
  getFhirDate,
  getFhirDateComponents,
  isISO1,
  isISO2,
  isYear,
  isYearMonthDay,
} from '@utils/fhir';

import type {
  AppointmentModel,
  AppointmentModelInput,
  StatusCode,
} from 'generated/types';
import { ParticipantStatusCode, ParticipantTypeCode } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type AppointmentValues = Pick<
  AppointmentRecord,
  | 'appointmentNotes'
  | 'diagnosis'
  | 'facilityCity'
  | 'facilityCountry'
  | 'facilityName'
  | 'facilityProvince'
  | 'followUps'
  | 'hcpName'
  | 'selfReported'
  | 'serviceType'
  | 'visitDate'
> & {
  // visitReason
  title: string;
  showTime: boolean;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & AppointmentValues;

export class AppointmentRecord extends FhirResource {
  protected model: AppointmentModel;
  public static readonly SERVICE_TYPE_SYSTEM =
    'http://terminology.hl7.org/CodeSystem/service-type';

  public constructor(model: AppointmentModel) {
    super(model);
    this.model = model;
  }

  public get facilityName() {
    return this.model.locationResource?.map(({ name }) => name).join('/');
  }

  public get facilityCity() {
    return this.model.locationResource
      ?.map(({ address }) => address?.city)
      .join('/');
  }

  public get facilityProvince() {
    return this.model.locationResource
      ?.map(({ address }) => address?.state)
      .join('/');
  }

  public get facilityCountry() {
    return this.model.locationResource
      ?.map(({ address }) => address?.country)
      .join('/');
  }

  public get hcpName() {
    return this.model.participant
      .filter(({ type }) =>
        type?.some((t) => t.coding?.some((c) => c.code === 'PPRF'))
      )
      .map(({ actor }) => actor?.display)
      .join('/');
  }

  public set hcpName(value: string | null | undefined) {
    const patientParticipant = this.model.participant.filter(({ type }) =>
      type?.some(({ coding }) =>
        coding?.some(({ code }) => code === ParticipantTypeCode.PAT)
      )
    );
    this.model = {
      ...this.model,
      participant: [
        ...patientParticipant,
        ...(value
          ? [
              {
                actor: { display: value },
                status: ParticipantStatusCode.accepted,
                type: [
                  {
                    text: 'Practitioner',
                    coding: [
                      {
                        code: ParticipantTypeCode.PPRF,
                        system: AppointmentRecord.VITALL_SYSTEM,
                      },
                    ],
                  },
                ],
              },
            ]
          : []),
      ],
    };
  }

  public get visitReason() {
    const { reasonCode } = this.model;
    return reasonCode?.map((r) => r.text).join(',');
  }

  public set visitReason(value: string | null | undefined) {
    this.model = {
      ...this.model,
      reasonCode: value
        ? [
            {
              text: value,
            },
          ]
        : null,
    };
  }

  public get appointmentType() {
    const { appointmentType } = this.model;
    return appointmentType?.coding?.map(({ display }) => display).join(',');
  }

  public get diagnosis() {
    const { extension } = this.model;
    return extension?.find((e) => e.url.endsWith(APPOINTMENTS_DIAGNOSIS))
      ?.valueString;
  }

  public set diagnosis(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: APPOINTMENTS_DIAGNOSIS,
        valueString: value,
      });
    } else {
      this.removeExtension(APPOINTMENTS_DIAGNOSIS);
    }
  }

  public get followUps() {
    const { patientInstruction } = this.model;
    return patientInstruction;
  }

  public set followUps(value: string | null | undefined) {
    this.model = {
      ...this.model,
      patientInstruction: value ?? null,
    };
  }

  public get appointmentNotes() {
    const { comment } = this.model;
    return comment;
  }

  public set appointmentNotes(value: string | null | undefined) {
    this.model = {
      ...this.model,
      comment: value ?? null,
    };
  }

  public get serviceType() {
    const { serviceType } = this.model;
    const type = serviceType?.at(0);
    const code = type?.coding?.at(0);
    return code?.code;
  }

  public set serviceType(value: string | null | undefined) {
    this.model = {
      ...this.model,
      ...(value && {
        serviceType: [
          {
            text: APPT_SERVICE_TYPE_MAP[value],
            coding: [
              {
                system: AppointmentRecord.SERVICE_TYPE_SYSTEM,
                code: value,
                display: APPT_SERVICE_TYPE_MAP[value],
              },
            ],
          },
        ],
      }),
    };
  }

  // TODO
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/class-literal-property-style
  public get referrals(): string | null {
    return null;
  }

  public get showTime() {
    const extension = this.findExtension(APPOINTMENTS_SHOW_VISIT_TIME);
    return extension?.valueBoolean ?? true;
  }

  public set showTime(value: boolean) {
    if (typeof value === 'boolean') {
      this.addOrReplaceExtension({
        url: APPOINTMENTS_SHOW_VISIT_TIME,
        valueBoolean: value,
      });
    } else {
      this.removeExtension(APPOINTMENTS_SHOW_VISIT_TIME);
    }
  }

  public get visitDate() {
    let date = this.model.start;
    const index = this.model.start?.indexOf('T');
    // Show time if this.showTime is true, null, or undefined
    date = this.showTime === false ? date?.slice(0, index) : date;
    const year = this.findExtension(APPOINTMENTS_YEAR);
    return date ?? year?.valueString;
  }

  public set visitDate(value: string | null | undefined) {
    if (value && isYear(value)) {
      this.model = {
        ...this.model,
        start: null,
        end: null,
      };
      this.addOrReplaceExtension({
        url: APPOINTMENTS_YEAR,
        valueString: value,
      });
    } else {
      this.removeExtension(APPOINTMENTS_YEAR);
      this.model = {
        ...this.model,
        start: value,
        end: dayjs(value).add(1, 'hour').toISOString(),
      };
    }
  }

  public getVisitDate() {
    let { start } = this.model;
    if (!start) {
      const extension = this.findExtension(APPOINTMENTS_YEAR);
      if (extension?.valueString) {
        start = extension.valueString;
      }
    }
    return getFhirDate(start);
  }

  public getVisitDateComponents() {
    return getFhirDateComponents(this.model.start);
  }

  public getVisitDateStr(showTime = false) {
    showTime = showTime ? this.showTime : showTime;
    let { start } = this.model;
    if (!start) {
      const extension = this.findExtension(APPOINTMENTS_YEAR);
      if (extension?.valueString) {
        start = extension.valueString;
      }
    }
    return formatFhirDate(start, { showTime });
  }

  public getModelInput(values?: AppointmentValues): AppointmentModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.visitReason = values.title;
      this.visitDate = AppointmentRecord.getAppointmentStart(values.visitDate);
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    delete cleaned.locationResource;
    return {
      ...cleaned,
      status: cleaned.status as StatusCode,
    };
  }

  public static getAppointmentStart(visitDate: string | null | undefined) {
    if (visitDate === undefined || visitDate === null) {
      return visitDate;
    }
    if (isISO1(visitDate) || isISO2(visitDate) || isYear(visitDate)) {
      return visitDate;
    } else if (isYearMonthDay(visitDate)) {
      return `${visitDate}T00:00:00.000Z`;
    }
  }
}
